import { motion } from "framer-motion";
import { Reveal, Chapter } from "./Reveal";
import { useT } from "../i18n";

const ORIGIN = { x: 797, y: 267 };
const ROUTES = [
  { x: 654, y: 180, testid: "route-middle-east" },
  { x: 512, y: 106, testid: "route-europe" },
  { x: 261, y: 144, testid: "route-americas" },
];

const arc = (to) => {
  const mx = (ORIGIN.x + to.x) / 2;
  const my = Math.min(ORIGIN.y, to.y) - 70;
  return `M ${ORIGIN.x} ${ORIGIN.y} Q ${mx} ${my} ${to.x} ${to.y}`;
};

const Markets = () => {
  const t = useT();
  return (
    <section id="markets" data-testid="markets-section" className="border-y border-gold/10 bg-[#0B132B] py-24 lg:py-32">
      <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
        <Reveal>
          <Chapter number="06" label={t.markets.label} testid="markets-chapter" />
        </Reveal>
        <div className="mt-8 flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
          <Reveal delay={0.1}>
            <h2 className="max-w-xl font-display text-3xl font-semibold leading-tight text-white sm:text-4xl">
              {t.markets.title}
            </h2>
          </Reveal>
          <Reveal delay={0.18}>
            <p className="max-w-md text-sm leading-relaxed text-slate-400">{t.markets.sub}</p>
          </Reveal>
        </div>

        <Reveal delay={0.2} className="mt-14">
          <div data-testid="global-map-canvas" className="relative aspect-[2/1] overflow-hidden border border-slate-800 bg-[#0B0E14]">
            <img
              src="/assets/world-map.svg"
              alt="Dotted world map with Indonesia highlighted in gold"
              className="absolute inset-0 h-full w-full opacity-90"
            />
            <svg viewBox="0 0 1000 500" className="absolute inset-0 h-full w-full" role="img" aria-label="Trade routes from Indonesia to world regions">
              {ROUTES.map((r, i) => (
                <g key={r.testid} data-testid={r.testid}>
                  <motion.path
                    d={arc(r)}
                    fill="none"
                    stroke="#D4AF37"
                    strokeWidth="1.2"
                    strokeDasharray="5 6"
                    initial={{ pathLength: 0, opacity: 0 }}
                    whileInView={{ pathLength: 1, opacity: 0.75 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.6, delay: 0.3 + i * 0.3, ease: "easeInOut" }}
                  />
                  <motion.circle
                    cx={r.x}
                    cy={r.y}
                    r="4"
                    fill="#D4AF37"
                    initial={{ opacity: 0, scale: 0 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 1.6 + i * 0.3 }}
                  />
                  <motion.text
                    x={r.x}
                    y={r.y - 14}
                    textAnchor="middle"
                    fill="#94A3B8"
                    fontSize="11"
                    fontFamily="JetBrains Mono, monospace"
                    letterSpacing="2"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 1.8 + i * 0.3 }}
                  >
                    {t.markets.routes[i]}
                  </motion.text>
                </g>
              ))}
              <motion.circle
                cx={ORIGIN.x}
                cy={ORIGIN.y}
                r="6"
                fill="#D4AF37"
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
              />
              <motion.circle
                cx={ORIGIN.x}
                cy={ORIGIN.y}
                r="6"
                fill="none"
                stroke="#D4AF37"
                strokeWidth="1"
                animate={{ r: [6, 20], opacity: [0.8, 0] }}
                transition={{ duration: 2.2, repeat: Infinity, ease: "easeOut" }}
              />
              <text x={ORIGIN.x} y={ORIGIN.y + 26} textAnchor="middle" fill="#F8FAFC" fontSize="12" fontFamily="JetBrains Mono, monospace" letterSpacing="2">
                {t.markets.origin}
              </text>
            </svg>
          </div>
        </Reveal>

        <div className="mt-8 flex flex-wrap gap-3">
          {t.markets.regions.map((r, i) => (
            <Reveal key={r} delay={0.04 * i}>
              <span
                data-testid={`market-region-${r.toLowerCase().replace(/\s+/g, "-")}`}
                className="border border-slate-700 px-4 py-2 font-mono text-[11px] tracking-[0.2em] text-slate-300 transition-[border-color,color] duration-300 hover:border-gold/50 hover:text-gold"
              >
                {r.toUpperCase()}
              </span>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.3}>
          <p className="mt-6 font-mono text-[10px] tracking-[0.18em] text-slate-500">{t.markets.disclaimer.toUpperCase()}</p>
        </Reveal>
      </div>
    </section>
  );
};

export default Markets;
